/**
 * Payment Rails — Partner Brand Assets
 *
 * IMPORTANT: These are THIRD-PARTY BRAND ASSETS, not icons.
 * Each payment rail logo has specific legal requirements from its owner.
 *
 * Before using any logo, review the brand guidelines at:
 * @see docs/brand-book/03-PARTNER-BRANDS.md
 *
 * Brand Owners & Guidelines:
 * - SEPA: European Payments Council (EPC034-08)
 * - SWIFT: SWIFT SCRL (requires approval for third-party use)
 * - PIX: Banco Central do Brasil
 * - UPI: NPCI (84-page brand guidelines, strict requirements)
 * - ACH: NACHA (written permission required)
 * - Faster Payments: Pay.UK
 * - SPEI/CoDi: Banco de México
 */

import { cn } from '@/lib/utils';
import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type PaymentRail =
  | 'pix'
  | 'sepa'
  | 'sepa-instant'
  | 'swift'
  | 'spei'
  | 'codi'
  | 'upi'
  | 'ach'
  | 'faster-payments'
  | 'wire';

export type PaymentRailSize = 'sm' | 'md' | 'lg' | 'xl';
export type PaymentRailVariant = 'color' | 'mono';

export interface PaymentRailLogoProps {
  /** The payment rail to display */
  rail: PaymentRail;
  /** Size preset: sm (24px), md (32px), lg (48px), xl (64px) */
  size?: PaymentRailSize;
  /** Color variant */
  variant?: PaymentRailVariant;
  /** Use wide/long version for horizontal layouts (SEPA, ACH only) */
  useLong?: boolean;
  /** Add clear space padding per brand guidelines */
  clearSpace?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export interface PaymentRailMeta {
  /** Short display name */
  name: string;
  /** Full official name */
  fullName: string;
  /** Country/region code */
  country: string;
  /** Geographic region */
  region: 'Americas' | 'Europe' | 'Asia Pacific' | 'Global';
  /** Expected transfer time */
  eta: string;
  /** Brief description */
  description: string;
  /** Brand owner organization */
  brandOwner: string;
  /** Link to brand guidelines (if public) */
  guidelinesUrl?: string;
  /** Whether third-party use requires explicit approval */
  requiresApproval: boolean;
  /** Minimum recommended size in pixels */
  minSize: number;
  /** Aspect ratio (width / height) */
  aspectRatio: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Size tokens in pixels */
const SIZE_MAP: Record<PaymentRailSize, number> = {
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
};

/** Aspect ratios from official SVG viewBox dimensions */
const ASPECT_RATIOS: Record<string, number> = {
  ach: 290 / 216,      // ~1.34:1
  sepa: 290 / 216,     // ~1.34:1
  wire: 408 / 216,     // ~1.89:1 (wide)
  default: 1.0,        // Square
};

/** Clear space multiplier (relative to height) per UPI guidelines */
const CLEAR_SPACE_MULTIPLIER = 0.15; // ~2mm equivalent at standard sizes

// =============================================================================
// REGISTRY
// =============================================================================

export const PAYMENT_RAILS_REGISTRY: Record<PaymentRail, PaymentRailMeta> = {
  pix: {
    name: 'PIX',
    fullName: 'PIX Instant Payment',
    country: 'BR',
    region: 'Americas',
    eta: 'Instant',
    description: 'Brazilian instant payment system',
    brandOwner: 'Banco Central do Brasil',
    guidelinesUrl: 'https://www.bcb.gov.br/estabilidadefinanceira/pix',
    requiresApproval: true,
    minSize: 24,
    aspectRatio: 1.0,
  },
  sepa: {
    name: 'SEPA',
    fullName: 'SEPA Bank Transfer',
    country: 'EU',
    region: 'Europe',
    eta: '1-2 business days',
    description: 'European bank transfers',
    brandOwner: 'European Payments Council',
    guidelinesUrl: 'https://www.europeanpaymentscouncil.eu/document-library/other/sepa-logo-visual-identity-guidelines',
    requiresApproval: false, // Public guidelines available
    minSize: 24,
    aspectRatio: 290 / 216,
  },
  'sepa-instant': {
    name: 'SEPA Instant',
    fullName: 'SEPA Instant Credit Transfer',
    country: 'EU',
    region: 'Europe',
    eta: 'Instant',
    description: 'European instant payments (SCT Inst)',
    brandOwner: 'European Payments Council',
    guidelinesUrl: 'https://www.europeanpaymentscouncil.eu/document-library/other/sepa-logo-visual-identity-guidelines',
    requiresApproval: false,
    minSize: 24,
    aspectRatio: 1.0,
  },
  swift: {
    name: 'SWIFT',
    fullName: 'SWIFT Wire Transfer',
    country: 'GLOBAL',
    region: 'Global',
    eta: '2-5 business days',
    description: 'International SWIFT wire transfers',
    brandOwner: 'SWIFT SCRL',
    guidelinesUrl: 'https://www.swift.com/about-us/legal/ipr-policies/swift-trademark-guidelines',
    requiresApproval: true, // CRITICAL: Requires SWIFT approval
    minSize: 32,
    aspectRatio: 1.5,
  },
  spei: {
    name: 'SPEI',
    fullName: 'SPEI Transfer',
    country: 'MX',
    region: 'Americas',
    eta: 'Same day',
    description: 'Mexican electronic payment system',
    brandOwner: 'Banco de México',
    guidelinesUrl: 'https://www.banxico.org.mx/',
    requiresApproval: true,
    minSize: 24,
    aspectRatio: 1.0,
  },
  codi: {
    name: 'CoDi',
    fullName: 'Cobro Digital',
    country: 'MX',
    region: 'Americas',
    eta: 'Instant',
    description: 'Mexican QR-based digital payment system',
    brandOwner: 'Banco de México',
    guidelinesUrl: 'https://www.banxico.org.mx/',
    requiresApproval: true,
    minSize: 24,
    aspectRatio: 1.0,
  },
  upi: {
    name: 'UPI',
    fullName: 'Unified Payments Interface',
    country: 'IN',
    region: 'Asia Pacific',
    eta: 'Instant',
    description: 'Indian instant payment system',
    brandOwner: 'NPCI (National Payments Corporation of India)',
    guidelinesUrl: 'https://www.npci.org.in/npci-in-news/brand-centre',
    requiresApproval: true, // Strict 84-page guidelines
    minSize: 24,
    aspectRatio: 1.0,
  },
  ach: {
    name: 'ACH',
    fullName: 'ACH Transfer',
    country: 'US',
    region: 'Americas',
    eta: '1-3 business days',
    description: 'US bank transfers',
    brandOwner: 'NACHA',
    guidelinesUrl: 'https://www.nacha.org/content/terms-and-conditions-use',
    requiresApproval: true, // Written permission required
    minSize: 24,
    aspectRatio: 290 / 216,
  },
  'faster-payments': {
    name: 'Faster Payments',
    fullName: 'Faster Payments Service',
    country: 'GB',
    region: 'Europe',
    eta: 'Instant',
    description: 'UK instant payment system',
    brandOwner: 'Pay.UK',
    guidelinesUrl: 'https://www.wearepay.uk/',
    requiresApproval: true,
    minSize: 24,
    aspectRatio: 1.0,
  },
  wire: {
    name: 'Wire',
    fullName: 'Wire Transfer',
    country: 'GLOBAL',
    region: 'Global',
    eta: '2-5 business days',
    description: 'International bank wire',
    brandOwner: 'Supabank (internal)',
    requiresApproval: false, // Internal asset
    minSize: 28, // Wide aspect ratio needs more height
    aspectRatio: 408 / 216,
  },
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get the SVG source path for a payment rail
 */
function getLogoSrc(
  rail: PaymentRail,
  variant: PaymentRailVariant,
  useLong: boolean,
  size: number
): string {
  const base = '/payment-methods';

  // Handle long variants for wide layouts
  if (useLong && (rail === 'sepa' || rail === 'ach') && size >= 48) {
    return `${base}/${rail}-long.svg`;
  }

  // Handle mono variants
  if (variant === 'mono') {
    return `${base}/${rail === 'sepa-instant' ? 'sepa-instant' : rail}-mono.svg`;
  }

  // Handle sepa-instant specifically
  if (rail === 'sepa-instant') {
    return `${base}/sepa-instant.svg`;
  }

  // Handle faster-payments
  if (rail === 'faster-payments') {
    return `${base}/fps.svg`;
  }

  return `${base}/${rail}.svg`;
}

/**
 * Calculate dimensions respecting aspect ratio
 */
function getDimensions(rail: PaymentRail, height: number): { width: number; height: number } {
  const meta = PAYMENT_RAILS_REGISTRY[rail];
  const aspectRatio = meta.aspectRatio;

  return {
    width: Math.round(height * aspectRatio),
    height,
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * PaymentRailLogo — Renders a payment rail brand asset with proper guidelines
 *
 * @example
 * // Basic usage
 * <PaymentRailLogo rail="sepa" size="md" />
 *
 * @example
 * // With clear space per brand guidelines
 * <PaymentRailLogo rail="upi" size="lg" clearSpace />
 *
 * @example
 * // Monochrome variant for limited color contexts
 * <PaymentRailLogo rail="pix" variant="mono" />
 */
export const PaymentRailLogo: React.FC<PaymentRailLogoProps> = ({
  rail,
  size = 'md',
  variant = 'color',
  useLong = false,
  clearSpace = false,
  className,
}) => {
  const meta = PAYMENT_RAILS_REGISTRY[rail];
  const heightPx = SIZE_MAP[size];

  // Respect minimum size per brand guidelines
  const effectiveHeight = Math.max(heightPx, meta.minSize);
  const { width, height } = getDimensions(rail, effectiveHeight);

  // Calculate clear space padding
  const padding = clearSpace ? Math.round(height * CLEAR_SPACE_MULTIPLIER) : 0;

  const src = getLogoSrc(rail, variant, useLong, effectiveHeight);

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        clearSpace && 'p-[var(--clearspace)]',
        className
      )}
      style={clearSpace ? { '--clearspace': `${padding}px` } as React.CSSProperties : undefined}
    >
      <img
        src={src}
        alt={meta.fullName}
        width={width}
        height={height}
        className={cn(
          'inline-block',
          '[transform:none]', // Protect from parent transforms (Canon rule)
        )}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          minWidth: `${width}px`,
          minHeight: `${height}px`,
          objectFit: 'contain',
          display: 'block',
          transform: 'none', // Double protection for brand assets
        }}
        loading="eager"
      />
    </span>
  );
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get metadata for a payment rail
 */
export function getPaymentRailMeta(rail: PaymentRail): PaymentRailMeta {
  return PAYMENT_RAILS_REGISTRY[rail];
}

/**
 * Check if a string is a valid payment rail
 */
export function isPaymentRail(value: string): value is PaymentRail {
  return value in PAYMENT_RAILS_REGISTRY;
}

/**
 * Get all supported payment rails
 */
export function getSupportedPaymentRails(): PaymentRail[] {
  return Object.keys(PAYMENT_RAILS_REGISTRY) as PaymentRail[];
}

/**
 * Get payment rails by region
 */
export function getPaymentRailsByRegion(region: PaymentRailMeta['region']): PaymentRail[] {
  return (Object.entries(PAYMENT_RAILS_REGISTRY) as [PaymentRail, PaymentRailMeta][])
    .filter(([, meta]) => meta.region === region)
    .map(([rail]) => rail);
}

/**
 * Check if a payment rail requires brand approval for use
 */
export function requiresBrandApproval(rail: PaymentRail): boolean {
  return PAYMENT_RAILS_REGISTRY[rail].requiresApproval;
}

// =============================================================================
// RE-EXPORTS FOR BACKWARD COMPATIBILITY
// =============================================================================

// Legacy type alias
export type PaymentMethod = PaymentRail;

// Legacy function aliases
export const getPaymentMethodIcon = (method: PaymentRail, _colored = true) => {
  // Return a function component for backward compatibility
  return (props: { className?: string; size?: number }) => (
    <PaymentRailLogo
      rail={method}
      size={props.size ? (props.size >= 48 ? 'lg' : props.size >= 32 ? 'md' : 'sm') : 'md'}
      className={props.className}
    />
  );
};

export const getPaymentMethodMeta = getPaymentRailMeta;
export const isPaymentMethod = isPaymentRail;
export const getSupportedPaymentMethods = getSupportedPaymentRails;

// Legacy registry alias
export const PAYMENT_METHOD_REGISTRY = PAYMENT_RAILS_REGISTRY;
